import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Row, Col, Modal, Button } from "react-bootstrap";

import api from "../../services/api";
import GlobalStyles from "../../assets/css/global";

import { toast } from 'react-toastify';

import CarregarPDF from "../../components/CarregarPDF";
import PainelCorrecaoIA from "../../components/PainelCorrecaoIA";

import { useCompetencia } from "../../context/competencia";

function Correction(props) {
  // eslint-disable-next-line no-unused-vars
  const { arquivo, id } = props;
  const history = useHistory();
  const location = useLocation();

  /*
    De onde o corretor veio: /redacoes, /correcao-ia-humano ou outra listagem.

    Lido do state do router E da query string, porque ao emendar na próxima
    redação a tela recarrega a página inteira (window.location.href), e isso
    descarta o state. Sem a query, a partir da segunda correção todo mundo caía
    em /redacoes — inclusive quem tinha vindo do IA + Humano.
  */
  const origem =
    location.state?.voltarPara ||
    new URLSearchParams(location.search).get("voltar") ||
    "/redacoes";
  const { competencia, setCompetencia } = useCompetencia();

  const [dadosRedacao, setDadosRedacao] = useState([]);
  const [nota1, setNota1] = useState(0);
  const [nota2, setNota2] = useState(0);
  const [nota3, setNota3] = useState(0);
  const [nota4, setNota4] = useState(0);
  const [nota5, setNota5] = useState(0);
  
  // eslint-disable-next-line no-unused-vars
  const [fixo, setFixo] = useState(true);
  const [falta, setFalta] = useState(false);
  const [observacao, setObservacao] = useState();
  const [texto, setTexto] = useState("FINALIZAR CORREÇÃO");

  /*
    Comentários que o corretor escolheu publicar para o aluno, no formato
    {c1: "...", c5: "..."}. Nascem da sugestão da IA mas são editáveis; para o
    aluno aparecem como "Comentários adicionais", sem menção à origem.
  */
  const [comentariosAdicionais, setComentariosAdicionais] = useState({});

  /*
    Como a correção estava quando a tela abriu.

    Serve para o botão de finalizar só liberar quando alguma coisa muda de fato.
    Sem isso, abrir uma redação já corrigida e clicar em finalizar registrava a
    pessoa como quem corrigiu por último, sem que ela tivesse mexido em nada — e
    esse registro falso aparecia no relatório como "editou correção de outro".
  */
  const [estadoOriginal, setEstadoOriginal] = useState(null);

  /*
    Marcações que ESTE corretor já fez nesta redação.

    Marcar é corrigir: quem marcou e veio finalizar não pode encontrar o botão
    travado só porque as notas ainda estão como vieram. Vem do servidor porque o
    corretor costuma passar pela tela do PDF no meio, perdendo o estado da aba.
  */
  const [marcacoesMinhas, setMarcacoesMinhas] = useState(0);

  /*
    Quem começou esta correção. Quando é outra pessoa, a tela avisa antes de o
    corretor mexer — ele continua podendo editar, mas sabendo que entra como
    editor e que o crédito segue com quem começou.
  */
  const [dono, setDono] = useState(null);
  const [souODono, setSouODono] = useState(true);
  /* modal de confirmação ao mexer na correção de outro corretor */
  const [confirmandoEdicao, setConfirmandoEdicao] = useState(false);
  const [finalizando, setFinalizando] = useState(false);
  const [avisoFechado, setAvisoFechado] = useState(false);

  /*
    Envio de fato. Fica separado do clique porque dois caminhos chegam aqui: o
    botão, quando a correção é minha, e o modal, quando é de outro corretor.
  */
  async function enviarFinalizacao() {
    setFinalizando(true);

    try {
      if (await finalizarCorrecao()) {
        setTexto("REDAÇÃO FINALIZADA, VOCÊ JÁ PODE SAIR");
        setConfirmandoEdicao(false);
      }
    } finally {
      setFinalizando(false);
    }
  }

  /* a correção é de outra pessoa e eu estou prestes a mexer nela */
  const corrigindoDeOutro = Boolean(dono) && !souODono;

  const dataDoDono = dono?.iniciada_em
    ? new Date(dono.iniciada_em).toLocaleDateString("pt-BR", { timeZone: "UTC" })
    : null;

  /*
    Alguma coisa mudou em relação ao que estava gravado?

    Enquanto a resposta for não, finalizar não faria diferença nenhuma na redação
    — só trocaria o nome de quem mexeu por último. Por isso o botão fica travado.
  */
  const notasAtuais = [nota1, nota2, nota3, nota4, nota5].map((n) =>
    Number.isFinite(n) ? String(n) : ""
  );

  const houveMudanca =
    !estadoOriginal ||
    marcacoesMinhas > 0 ||
    JSON.stringify(notasAtuais) !== JSON.stringify(estadoOriginal.notas) ||
    Boolean(falta) !== estadoOriginal.falta ||
    (observacao || "") !== estadoOriginal.observacao ||
    JSON.stringify(comentariosAdicionais) !== estadoOriginal.comentarios;

  /*
    Devolve true quando a correção foi de fato enviada. O botão usa isso para só
    então dizer que finalizou — antes ele anunciava "REDAÇÃO FINALIZADA" mesmo
    quando nada tinha sido salvo.
  */
  async function finalizarCorrecao() {
    /*
      Sem nota escolhida, `nota1..5` são NaN (vêm de parseInt(null) quando a redação
      ainda não foi corrigida). NaN vira null no JSON e é gravado como null: o aluno
      via a competência em branco e o total aparecia como "Total: NaN".
      Aqui isso é barrado, dizendo quais faltam.
    */
    const naoPreenchidas = [nota1, nota2, nota3, nota4, nota5]
      .map((n, i) => (Number.isFinite(n) ? null : i + 1))
      .filter(Boolean);

    // redação anulada vale 0 em tudo, então não faz sentido exigir as marcações
    if (!falta && naoPreenchidas.length > 0) {
      toast.error(
        `Marque a pontuação de todas as competências antes de finalizar. ` +
        `Faltando: ${naoPreenchidas.join(", ")}.`,
        { autoClose: 8000 }
      );
      return false;
    }

    try {
      const dataCorrection = {
        // anulada: zera as cinco, que é o que o total já exibe na tela
        competencia1: falta ? 0 : nota1,
        competencia2: falta ? 0 : nota2,
        competencia3: falta ? 0 : nota3,
        competencia4: falta ? 0 : nota4,
        competencia5: falta ? 0 : nota5,
        falta: falta,
        id: id,
        observacao: observacao,
        comentarios_adicionais: comentariosAdicionais
      };

      let response = await api.post("/redacaoUpdate", dataCorrection);

      if (response?.data?.message === "Redação atualizada com sucesso") {
          toast.success("Correção efetuada, você será redirecionado para a próxima correção de mesmo tema e turma.");
          setCompetencia(0);
          /* leva a origem adiante: a recarga abaixo apagaria o state do router */
          window.location.href =
            `/corrigir/redacao/${response.data.id}?voltar=${encodeURIComponent(origem)}`;
          return true;
      }

      if (response?.data?.message === "Redação finalizada") {
        toast("Correção efetuada, você não possui mais correções de mesmo tema/turma, você voltará para a página de redações.");
        history.push(origem);
        return true;
      }

      /*
        Antes havia `catch {}` vazio e `finally { return true }`: qualquer falha era
        engolida e a tela anunciava "REDAÇÃO FINALIZADA" sem nada ter sido salvo.
      */
      console.error("Resposta inesperada ao finalizar a correção:", response);
      toast.error(
        "Não foi possível confirmar o salvamento. Confira a nota na listagem antes de sair desta tela.",
        { autoClose: 10000 }
      );
      return false;
    } catch (err) {
      console.error("Erro ao finalizar a correção:", err);
      toast.error("Erro ao finalizar a correção. Tente novamente.");
      return false;
    }
  }

  useEffect(() => {
    async function getCorrecao(){
        const data = {
          id: id
        };
    
        const retorno = await api.post("/getCorrecao", data);
        setDadosRedacao(retorno.data.dadosRedacao);
        setMarcacoesMinhas(retorno.data.marcacoesDoCorretor || 0);
        setDono(retorno.data.dono || null);
        setSouODono(retorno.data.souODono !== false);
  
        setNota1(retorno.data.dadosRedacao[0] ? parseInt(retorno.data.dadosRedacao[0].competencia1) : 0 );
        setNota2(retorno.data.dadosRedacao[0] ? parseInt(retorno.data.dadosRedacao[0].competencia2) : 0 );
        setNota3(retorno.data.dadosRedacao[0] ? parseInt(retorno.data.dadosRedacao[0].competencia3) : 0 );
        setNota4(retorno.data.dadosRedacao[0] ? parseInt(retorno.data.dadosRedacao[0].competencia4) : 0 );
        setNota5(retorno.data.dadosRedacao[0] ? parseInt(retorno.data.dadosRedacao[0].competencia5) : 0 );
        setFalta(retorno.data.dadosRedacao[0] ? retorno.data.dadosRedacao[0].falta : false );

        /*
          A observação já vinha na resposta, mas a tela não a carregava: ao reabrir
          uma correção, a caixa aparecia vazia mesmo havendo texto gravado.
        */
        setObservacao(retorno.data.dadosRedacao[0]?.observacao || "");

        /*
          Ao reabrir uma correção já finalizada, recupera o que foi publicado para
          o aluno — senão finalizar de novo apagaria os comentários. A coluna é JSON;
          o driver às vezes devolve objeto, às vezes string.
        */
        const publicados = retorno.data.dadosRedacao[0]?.comentarios_adicionais;

        let adicionais = {};

        if (publicados) {
          try {
            adicionais = typeof publicados === "string" ? JSON.parse(publicados) : publicados;
          } catch {
            adicionais = {};
          }

          setComentariosAdicionais(adicionais);
        }

        const linha = retorno.data.dadosRedacao[0];

        setEstadoOriginal({
          /*
            Guardado como texto para a comparação não tropeçar em "160" x 160.
            Em redação ainda não corrigida as notas vêm nulas e qualquer marcação
            já conta como mudança — que é o certo: ali a correção começa do zero.
          */
          notas: [1, 2, 3, 4, 5].map((n) => String(linha?.[`competencia${n}`] ?? "")),
          falta: Boolean(linha?.falta),
          observacao: linha?.observacao || "",
          comentarios: JSON.stringify(adicionais),
        });
    }

    getCorrecao();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <GlobalStyles />
      <Col className="correcoes">
        {/*
          Aviso de que a correção já tem dono.

          Preso no topo da janela, e não no fluxo da página, porque o painel do
          PDF é `position: fixed` com z-index 1001 e cobre tudo que está embaixo
          — justamente durante as marcações, que é quando o corretor precisa ler
          isto. Daí o z-index acima do painel e o botão de fechar.
        */}
        {corrigindoDeOutro && !avisoFechado && (
          <div
            className="alert alert-warning d-flex align-items-center mb-0"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1100,
              borderRadius: 0,
              borderLeft: "6px solid #f39200",
              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
            }}
            role="alert"
          >
            <i className="fas fa-triangle-exclamation mr-2" />
            <div style={{ flex: 1, fontSize: "14px" }}>
              <strong>Esta correção já tem dono: {dono.nome}.</strong>{" "}
              {dono.finalizada ? "Finalizada" : "Iniciada"}
              {dataDoDono ? ` em ${dataDoDono}` : ""}. Você pode marcar e ajustar as notas,
              mas o crédito continua sendo dele — o que você fizer fica registrado como edição.
            </div>
            <button
              type="button"
              className="btn btn-sm btn-link text-dark"
              onClick={() => setAvisoFechado(true)}
              title="Fechar aviso"
            >
              ×
            </button>
          </div>
        )}

        <Row>
          <Col xs={12} md>
            {arquivo && (
              <CarregarPDF
                pdf={`https://redacoes.nyc3.digitaloceanspaces.com/${arquivo}`}
                setFixo={setFixo}
                competencia={competencia}
                id={id}
                dadosRedacao={dadosRedacao}
              />
            )}
            <hr/>
            <p>
              <strong>
                Uma vez que a correção com as observações da redação/pdf forem
                finalizadas, você agora poderá marcar as observações e pontuação
                da redação para finalizar a correção.
              </strong>
            </p>
          </Col>
        </Row>

        <Row>
          <Col>
            <table className="table table-striped table-bordered">
              <tr>
                <td
                  onClick={() => setFalta(falta === "1" ? false : "1")}
                  className={`clicavel ${falta === "1" && "marcado"}`}
                >
                  Texto Insuficiente
                </td>
                <td
                  onClick={() => setFalta(falta === "2" ? false : "2")}
                  className={`clicavel ${falta === "2" && "marcado"}`}
                >
                  Cópia
                </td>
                <td
                  onClick={() => setFalta(falta === "3" ? false : "3")}
                  className={`clicavel ${falta === "3" && "marcado"}`}
                >
                  Fuga ao Tema
                </td>
                <td
                  onClick={() => setFalta(falta === "4" ? false : "4")}
                  className={`clicavel ${falta === "4" && "marcado"}`}
                >
                  Fuga ao Gênero
                </td>
                <td
                  onClick={() => setFalta(falta === "5" ? false : "5")}
                  className={`clicavel ${falta === "5" && "marcado"}`}
                >
                  Fere Direitos Humanos
                </td>
                <td
                  onClick={() => setFalta(falta === "6" ? false : "6")}
                  className={`clicavel ${falta === "6" && "marcado"}`}
                >
                  Em Branco
                </td>
                <td
                  onClick={() => setFalta(falta === "7" ? false : "7")}
                  className={`clicavel ${falta === "7" && "marcado"}`}
                >
                  Outras Formas de Anulação
                </td>
              </tr>
            </table>
            <PainelCorrecaoIA
              redacaoId={id}
              notas={[nota1, nota2, nota3, nota4, nota5]}
              onUsarNota={(indice, valor) => {
                const aplicar = [setNota1, setNota2, setNota3, setNota4, setNota5][indice - 1];
                if (aplicar) aplicar(valor);
              }}
              comentarios={comentariosAdicionais}
              onComentariosChange={setComentariosAdicionais}
            />

            <table className="table table-striped table-bordered">
              <tr>
                <td>
                  <h3>RESERVADO AO CORRETOR</h3>
                </td>
                <td>
                  N<sup>0</sup>
                  <br />0
                </td>
                <td>
                  N<sup>1</sup>
                  <br />
                  40
                </td>
                <td>
                  N<sup>2</sup>
                  <br />
                  80
                </td>
                <td>
                  N<sup>3</sup>
                  <br />
                  120
                </td>
                <td>
                  N<sup>4</sup>
                  <br />
                  160
                </td>
                <td>
                  N<sup>5</sup>
                  <br />
                  200
                </td>
              </tr>
              <tr>
                <td>
                  1 - Demonstrar domínio da norma culta da língua escrita.{" "}
                </td>
                <td>
                  <button
                    className={nota1 === 0 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota1(0)}
                  >
                    {nota1 === 0 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota1 === 40 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota1(40)}
                  >
                    {nota1 === 40 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota1 === 80 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota1(80)}
                  >
                    {nota1 === 80 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota1 === 120 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota1(120)}
                  >
                    {nota1 === 120 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota1 === 160 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota1(160)}
                  >
                    {nota1 === 160 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota1 === 200 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota1(200)}
                  >
                    {nota1 === 200 && `X`}
                  </button>
                </td>
              </tr>
              <tr>
                <td>
                  2 - Compreender a proposta de redação e aplicar conceitos de
                  várias áreas de conhecimento para desenvolver o tema, dentro
                  dos limites estruturais do texto dissertativo-argumentativo.
                </td>
                <td>
                  <button
                    className={nota2 === 0 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota2(0)}
                  >
                    {nota2 === 0 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota2 === 40 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota2(40)}
                  >
                    {nota2 === 40 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota2 === 80 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota2(80)}
                  >
                    {nota2 === 80 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota2 === 120 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota2(120)}
                  >
                    {nota2 === 120 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota2 === 160 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota2(160)}
                  >
                    {nota2 === 160 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota2 === 200 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota2(200)}
                  >
                    {nota2 === 200 && `X`}
                  </button>
                </td>
              </tr>
              <tr>
                <td>
                  3 - Selecionar, relacionar, organizar, e interpretar
                  informações, fatos, opiniões e argumentos em defesa do ponto
                  de vista.
                </td>
                <td>
                  <button
                    className={nota3 === 0 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota3(0)}
                  >
                    {nota3 === 0 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota3 === 40 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota3(40)}
                  >
                    {nota3 === 40 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota3 === 80 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota3(80)}
                  >
                    {nota3 === 80 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota3 === 120 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota3(120)}
                  >
                    {nota3 === 120 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota3 === 160 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota3(160)}
                  >
                    {nota3 === 160 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota3 === 200 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota3(200)}
                  >
                    {nota3 === 200 && `X`}
                  </button>
                </td>
              </tr>
              <tr>
                <td>
                  4 - Demonstrar conhecimento dos mecanismos linguísticos
                  necessários para a construção da argumentação
                </td>
                <td>
                  <button
                    className={nota4 === 0 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota4(0)}
                  >
                    {nota4 === 0 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota4 === 40 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota4(40)}
                  >
                    {nota4 === 40 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota4 === 80 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota4(80)}
                  >
                    {nota4 === 80 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota4 === 120 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota4(120)}
                  >
                    {nota4 === 120 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota4 === 160 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota4(160)}
                  >
                    {nota4 === 160 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota4 === 200 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota4(200)}
                  >
                    {nota4 === 200 && `X`}
                  </button>
                </td>
              </tr>
              <tr>
                <td>
                  5 - Elaborar proposta de solução para o problema abordado,
                  mostrando respeito aos valores humanos e considerando a
                  diversidade sociocultural.
                </td>
                <td>
                  <button
                    className={nota5 === 0 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota5(0)}
                  >
                    {nota5 === 0 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota5 === 40 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota5(40)}
                  >
                    {nota5 === 40 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota5 === 80 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota5(80)}
                  >
                    {nota5 === 80 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota5 === 120 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota5(120)}
                  >
                    {nota5 === 120 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota5 === 160 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota5(160)}
                  >
                    {nota5 === 160 && `X`}
                  </button>
                </td>
                <td>
                  <button
                    className={nota5 === 200 ? `botaoNota ativo` : "botaoNota"}
                    onClick={() => setNota5(200)}
                  >
                    {nota5 === 200 && `X`}
                  </button>
                </td>
              </tr>
            </table>

            <hr />

            <table className="table table-striped table-bordered">
              <tr>
                <td className="text-right">
                  <h3>
                    <strong>Total: </strong>{" "}
                    {falta ? (
                      <>0</>
                    ) : (
                      /* competência não marcada é NaN; some no total em vez de
                         contaminá-lo, e um aviso diz que ainda falta marcar */
                      <>
                        {[nota1, nota2, nota3, nota4, nota5].reduce(
                          (t, n) => t + (Number.isFinite(n) ? n : 0),
                          0
                        )}
                        {[nota1, nota2, nota3, nota4, nota5].some((n) => !Number.isFinite(n)) && (
                          <small className="text-danger d-block" style={{ fontSize: "13px" }}>
                            Faltam competências sem pontuação
                          </small>
                        )}
                      </>
                    )}
                  </h3>
                </td>
              </tr>
            </table>

            <hr />

            <table className="table table-striped table-bordered">
              <tr>
                <td>
                  <textarea
                    onChange={(e) => setObservacao(e.target.value)}
                    className="form-control"
                    placeholder="Digite sua observação ao aluno"
                    value={observacao}
                  ></textarea>
                  <hr />

                  <button
                    onClick={() => {
                      /*
                        Mexendo na correção de outro: confirma no modal. O aviso
                        do topo já está na tela, mas quem corrige em sequência
                        passa batido por faixa — na hora de gravar, não passa.
                      */
                      if (corrigindoDeOutro) {
                        setConfirmandoEdicao(true);
                        return;
                      }

                      enviarFinalizacao();
                    }}
                    disabled={!houveMudanca}
                    title={
                      houveMudanca
                        ? "Finalizar a correção desta redação"
                        : "Nada foi alterado nesta correção. Faça uma marcação na redação, marque uma nota, escreva uma observação ou anule a redação para poder finalizar."
                    }
                    className="btn btn-danger pull-right"
                  >
                    {texto}
                  </button>
                </td>
              </tr>
            </table>

            <h4 className="text-center">
              Rua Gavião Peixoto 124- Sala 509 -Edifício Manhattan -
              2601-0195/ (21) 96957-0209
              <br />
              cursodaniellevelasco@gmail.com{" "}
            </h4>
          </Col>
        </Row>
      </Col>

      {/*
        Confirmação de edição da correção de outro corretor.

        Modal e não toast: o toast some sozinho e não exige resposta, e aqui a
        ideia é parar o corretor por um instante antes de gravar. O z-index do
        modal do Bootstrap (1050) já fica acima do painel do PDF (1001), então
        ele aparece mesmo com o PDF aberto.
      */}
      <Modal
        show={confirmandoEdicao}
        onHide={() => !finalizando && setConfirmandoEdicao(false)}
        centered
      >
        <Modal.Header closeButton className="bg-warning">
          <Modal.Title style={{ fontSize: "1.1rem" }}>
            <i className="fas fa-triangle-exclamation mr-2" />
            Esta correção é de outro corretor
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-2">
            A correção desta redação foi {dono?.finalizada ? "finalizada" : "iniciada"} por{" "}
            <strong>{dono?.nome}</strong>
            {dataDoDono ? ` em ${dataDoDono}` : ""}.
          </p>
          <p className="mb-0">
            Ao finalizar, você fica registrado como <strong>quem editou</strong> — o
            crédito da correção continua sendo dele.
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => setConfirmandoEdicao(false)}
            disabled={finalizando}
          >
            Cancelar
          </Button>
          <Button variant="danger" onClick={enviarFinalizacao} disabled={finalizando}>
            {finalizando ? "Finalizando…" : "Confirmar e finalizar"}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className={`fixar none`}>
        <div
          className={`botao cor`}
          style={{
            backgroundColor: `${
              competencia === 1 ? "rgba(177,0,168, 1)" : "rgba(177,0,168, 0.3)"
            }`,
            color: `${competencia === 1 ? "#fff" : "#000"}`,
            fontWeight: `${competencia === 1 ? "bold" : ""}`,
          }}
          onClick={() => {
            setCompetencia(1);
          }}
        >
          {dadosRedacao.tipo == 1 ? 'Modalidade culta da língua' :'Competência 1'}
        </div>
        <div
          className={`botao cor`}
          style={{
            backgroundColor: `${
              competencia === 2 ? "rgba(255,0,0,1)" : "rgba(255,0,0,0.3)"
            }`,
            color: `${competencia === 2 ? "#fff" : "#000"}`,
            fontWeight: `${competencia === 2 ? "bold" : ""}`,
          }}
          onClick={() => {
            setCompetencia(2);
          }}
        >
          {dadosRedacao.tipo == 1 ? 'Tipologia textual' :'Competência 2'}
        </div>
        <div
          className={`botao cor`}
          style={{
            backgroundColor: `${
              competencia === 3 ? "rgba(29,29,245, 1)" : "rgba(29,29,245,0.3)"
            }`,
            color: `${competencia === 3 ? "#fff" : "#000"}`,
            fontWeight: `${competencia === 3 ? "bold" : ""}`,
          }}
          onClick={() => {
            setCompetencia(3);
          }}
        >
          {dadosRedacao.tipo == 1 ? 'Argumentação' :'Competência 3'}
        </div>
        <div
          className={`botao cor`}
          style={{
            backgroundColor: `${
              competencia === 4 ? "rgba(10,208,0, 1)" : "rgba(10,208,0,0.3)"
            }`,
            color: `${competencia === 4 ? "#fff" : "#000"}`,
            fontWeight: `${competencia === 4 ? "bold" : ""}`,
          }}
          onClick={() => {
            setCompetencia(4);
          }}
        >
          {dadosRedacao.tipo == 1 ? 'Coesão e Estrura do período' :'Competência 4'}
        </div>
        <div
          className={`botao cor`}
          style={{
            backgroundColor: `${
              competencia === 5 ? "rgba(208,201,0, 1)" : "rgba(208,201,0,0.3)"
            }`,
            color: `${competencia === 5 ? "#fff" : "#000"}`,
            fontWeight: `${competencia === 5 ? "bold" : ""}`,
          }}
          onClick={() => {
            setCompetencia(5);
          }}
        >
          {dadosRedacao.tipo == 1 ? 'Adequação ao tema' :'Competência 5'}
        </div>
      </div>
    </>
  );
}

export default Correction;
